import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import AssetStatusBadge from '@/components/asset-status-badge/AssetStatusBadge';
import Link from '@/components/link';
import { useGetContributorsFieldConfig } from '@/utils/table/hooks/useGetContributorsFieldConfig';
import { useGetDepartmentFieldConfig } from '@/utils/table/hooks/useGetDepartmentFieldConfig';
import { useGetOwnersFieldConfig } from '@/utils/table/hooks/useGetOwnersFieldConfig';
import type {
  StatefulTableOptions,
  UseGetTablePropsOptions,
} from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetStatelessTableProps } from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetTableProps } from '@/utils/table/hooks/useGetTableProps';
import { useGetTablePropsWithoutUrlHash } from '@/utils/table/hooks/useGetTablePropsWithoutUrlHash';
import { useGetTagFieldConfig } from '@/utils/table/hooks/useGetTagFieldConfig';
import { exportStyleFromOption } from '@/utils/table/pdfExportStyles';
import type { TableFields, TablePropsWithActions } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';
import { assetDetailsUrl } from '@/utils/urls';

import type { AssetFields, AssetTableFields } from './types';
import { useLabelledFields } from './useLabelledFields';

const useGetFieldConfig = ({
  useAbsoluteUrls,
}: {
  useAbsoluteUrls?: boolean;
}): TableFields<AssetTableFields> => {
  const allOwners = useGetOwnersFieldConfig<AssetTableFields>({
    formId: 'asset',
    fieldId: 'Owners',
  });
  const allContributors = useGetContributorsFieldConfig<AssetTableFields>({
    formId: 'asset',
    fieldId: 'Contributors',
  });
  const tagField = useGetTagFieldConfig<AssetTableFields>({
    formId: 'asset',
    fieldId: 'tags',
  });
  const departmentField = useGetDepartmentFieldConfig<AssetTableFields>(
    (r) => r.departments,
    {
      formId: 'asset',
      fieldId: 'departments',
    }
  );
  const { getByValue: getAssetStatus, options: assetStatusOptions } =
    useRating('asset_status');
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'assets.columns',
  });
  const { t } = useTranslation(['common'], {
    keyPrefix: 'columns',
  });

  return useMemo(
    () => ({
      SequentialIdLabel: { header: t('id'), sortingField: 'SequentialId' },
      Title: {
        formId: 'asset',
        fieldId: 'Title',
        cell: (item) => (
          <Link
            variant={'secondary'}
            href={useAbsoluteUrls ? assetDetailsUrl(item.Id) : item.Id}
            isRelativeUrl={!useAbsoluteUrls}
          >
            {item.Title}
          </Link>
        ),
        isRowHeader: true,
      },
      allOwners,
      allContributors,
      AssetType: { formId: 'asset', fieldId: 'AssetType' },
      SerialNumber: { formId: 'asset', fieldId: 'SerialNumber' },
      Location: { formId: 'asset', fieldId: 'Location' },
      Cost: { formId: 'asset', fieldId: 'Cost' },
      DepreciationMethod: { formId: 'asset', fieldId: 'DepreciationMethod' },
      AssigneeName: { header: st('assignee') },
      PurchaseDate: dateColumnFromConfig({
        header: { formId: 'asset', fieldId: 'PurchaseDate' },
        dateField: 'PurchaseDate',
      }),
      StatusLabelled: {
        formId: 'asset',
        fieldId: 'Status',
        cell: (item) => (
          <AssetStatusBadge
            item={{
              Status: item.Status,
            }}
          />
        ),
        filterOptions: {
          filteringOptions: assetStatusOptions
            .map((o) => o.label)
            .filter((label): label is string => Boolean(label))
            .map((label) => ({ value: label, label })),
        },
        exportCellStyle: exportStyleFromOption((item) =>
          getAssetStatus(item.Status)
        ),
      },
      tags: tagField,
      departments: departmentField,
      Id: { header: t('guid') },
      ModifiedAtTimestamp: dateColumnFromConfig({
        header: { header: t('updated_on') },
        dateField: 'ModifiedAtTimestamp',
      }),
      ModifiedByUser: { header: t('updated_by_id') },
      ModifiedByUserName: { header: st('modified_by_username') },
      CreatedAtTimestamp: dateColumnFromConfig({
        header: { header: t('created_on') },
        dateField: 'CreatedAtTimestamp',
      }),
      CreatedByUserName: { header: st('created_by_username') },
      Description: { formId: 'asset', fieldId: 'Description' },
    }),
    [
      allContributors,
      allOwners,
      departmentField,
      getAssetStatus,
      assetStatusOptions,
      st,
      t,
      tagField,
      useAbsoluteUrls,
    ]
  );
};

const useGetAssetTableProps = (
  records: AssetFields[] | undefined,
  useAbsoluteUrls: boolean = false
): UseGetTablePropsOptions<AssetTableFields> => {
  const labelledFields = useLabelledFields(records);
  const { t } = useTranslation(['common'], { keyPrefix: 'assets' });
  const fields = useGetFieldConfig({ useAbsoluteUrls });

  return {
    tableId: 'assetRegister',
    data: labelledFields,
    entityLabel: t('entity_name'),
    emptyCollectionAction: <></>,
    preferencesStorageKey: 'AssetsRegisterTable-PreferencesV1',
    enableFiltering: true,
    initialColumns: [
      'Title',
      'allOwners',
      'AssetType',
      'SerialNumber',
      'PurchaseDate',
      'Location',
      'StatusLabelled',
      'tags',
    ],
    fields,
    defaultSortingState: {
      sortingColumn: 'PurchaseDate',
      sortingDirection: 'desc',
    },
    customAttributeFormIds: ['asset'],
  };
};

export const useGetCollectionTableProps = (
  records: AssetFields[] | undefined
): TablePropsWithActions<AssetTableFields> => {
  const props = useGetAssetTableProps(records);

  return useGetTableProps(props);
};

export const useGetCollectionStatelessTableProps = (
  records: AssetFields[] | undefined,
  useAbsoluteUrls?: boolean
): TablePropsWithActions<AssetTableFields> => {
  const props = useGetAssetTableProps(records, useAbsoluteUrls);

  return useGetTablePropsWithoutUrlHash(props);
};

export const useGetAssetSmartWidgetTableProps = (
  records: AssetFields[] | undefined,

  statefulTableOptions: StatefulTableOptions<AssetTableFields>
): TablePropsWithActions<AssetTableFields> => {
  const props = useGetAssetTableProps(records, true);

  return useGetStatelessTableProps<AssetTableFields>({
    ...props,
    ...statefulTableOptions,
    enableFiltering: false,
  });
};
